import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { List, X, ShieldCheck, Phone, CaretDown } from "@phosphor-icons/react";
import { AGENCY } from "@/lib/api";
import { SOLUTIONS } from "@/data/solutions";

const SECTIONS = [
  { label: "Home", id: "hero" },
  { label: "Life Insurance", id: "life" },
  { label: "Health Insurance", id: "health" },
  { label: "About Us", id: "about" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (onHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const solid = scrolled || !onHome;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-warm-white/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <button data-testid="logo-button" onClick={() => goSection("hero")} className="flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-navy text-gold">
            <ShieldCheck weight="fill" size={20} />
          </span>
          <span className="font-heading font-extrabold text-[17px] tracking-tight text-navy leading-none">
            {AGENCY.brand}
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {SECTIONS.slice(0, 3).map((l) => (
            <NavBtn key={l.id} onClick={() => goSection(l.id)} testid={`nav-${l.id}`}>{l.label}</NavBtn>
          ))}

          {/* Solutions dropdown */}
          <div className="relative" onMouseEnter={() => setSolOpen(true)} onMouseLeave={() => setSolOpen(false)}>
            <button data-testid="nav-solutions"
              onClick={() => goSection("solutions")}
              className="flex items-center gap-1 text-[14px] font-medium text-navy/70 hover:text-navy transition-colors">
              Solutions <CaretDown size={13} weight="bold" className={`transition-transform ${solOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {solOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-full pt-3 w-72"
                >
                  <div className="rounded-xl border border-black/5 bg-warm-white/95 backdrop-blur-xl shadow-xl shadow-navy/10 p-2">
                    {SOLUTIONS.map((s) => {
                      const Icon = s.icon;
                      return (
                        <Link key={s.key} to={`/solutions/${s.key}`} data-testid={`nav-solution-${s.key}`}
                          onClick={() => setSolOpen(false)}
                          className="flex items-start gap-3 rounded-lg p-3 hover:bg-navy/[0.05] transition-colors">
                          <span className="grid place-items-center w-8 h-8 shrink-0 rounded-md bg-navy/[0.06] text-navy">
                            <Icon weight="duotone" size={18} />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-navy">{s.audience.replace("For ", "")}</span>
                            <span className="block text-xs text-brand-slate mt-0.5 leading-snug">{s.points[0].name}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavBtn onClick={() => goSection("about")} testid="nav-about">About Us</NavBtn>
          <Link to="/blog" data-testid="nav-blog"
            className="text-[14px] font-medium text-navy/70 hover:text-navy transition-colors">Blog</Link>
          <NavBtn onClick={() => goSection("contact")} testid="nav-contact">Contact</NavBtn>
        </nav>

        <div className="flex items-center gap-3">
          <a href={AGENCY.phoneHref} data-testid="header-phone"
            className="hidden md:inline-flex items-center gap-2 text-[14px] font-semibold text-navy hover:text-gold transition-colors">
            <Phone weight="fill" size={16} className="text-gold" />
            {AGENCY.phone}
          </a>
          <button data-testid="header-quote-cta" onClick={() => goSection("hero")}
            className="hidden sm:inline-flex items-center rounded-full bg-navy px-6 py-2.5 text-[14px] font-semibold text-warm-white transition-all hover:bg-slate-navy hover:shadow-lg hover:shadow-navy/20 active:scale-95">
            Get a Free Quote
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center w-10 h-10 rounded-md text-navy">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            data-testid="mobile-nav"
            className="lg:hidden overflow-hidden bg-warm-white/95 backdrop-blur-xl border-b border-black/5"
          >
            <div className="px-6 py-4 flex flex-col">
              {SECTIONS.slice(0, 3).map((l) => (
                <MobileBtn key={l.id} onClick={() => goSection(l.id)} testid={`mobile-nav-${l.id}`}>{l.label}</MobileBtn>
              ))}
              {SOLUTIONS.map((s) => (
                <Link key={s.key} to={`/solutions/${s.key}`} onClick={() => setOpen(false)}
                  data-testid={`mobile-solution-${s.key}`}
                  className="py-3 text-navy font-medium border-b border-black/5 pl-3 text-sm">
                  {s.audience.replace("For ", "")}
                </Link>
              ))}
              <MobileBtn onClick={() => goSection("about")} testid="mobile-nav-about">About Us</MobileBtn>
              <Link to="/blog" onClick={() => setOpen(false)} data-testid="mobile-nav-blog"
                className="py-3 text-navy font-medium border-b border-black/5">Blog</Link>
              <MobileBtn onClick={() => goSection("contact")} testid="mobile-nav-contact">Contact</MobileBtn>
              <button onClick={() => goSection("hero")}
                className="mt-4 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-warm-white">
                Get a Free Quote
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

function NavBtn({ children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-[14px] font-medium text-navy/70 hover:text-navy transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-gold after:transition-all hover:after:w-full">
      {children}
    </button>
  );
}

function MobileBtn({ children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left py-3 text-navy font-medium border-b border-black/5">
      {children}
    </button>
  );
}
